import { Injectable } from '@angular/core';
import {
  arrayRemove,
  arrayUnion,
  collection,
  collectionData,
  doc,
  DocumentData,
  documentId,
  Firestore,
  getDocs,
  orderBy,
  Query,
  query,
  Timestamp,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { getVideo$, getVideosFromSource, VideoSourceQueryOption, VideoWithDocId } from '@yoimo/client-sdk/videos';
import { VideoAccessLogEntry, VideoListSource } from '@yoimo/interfaces';
import { chunk } from 'lodash';
import { lastValueFrom, map, Observable, Subject, EMPTY } from 'rxjs';
import { Stream } from '../home/channels-view/channel-videos/channel-streams/channel-streams.component';
import { ReservedInputParam } from './bulk-import.service';
import dayjs from './dayjs.config';

export enum VideoCategoryId {
  SHORT = 'SHORT',
  UPLOAD = 'UPLOAD',
  STREAM = 'STREAM',
  RATING = 'RATING',
}

export interface Videos {
  fileName: string;
  href: string;
  src: string;
}
enum VideoStatusId {
  ACTIVE = 'ACTIVE',
  SCHEDULED = 'SCHEDULED',
  ARCHIVED = 'ARCHIVED',
  BATCHUPLOADS = 'BATCHUPLOADS',
}

export type videoType = 'UPLOAD' | 'SHORT';

@Injectable({
  providedIn: 'root',
})
export class VideosService {
  searchText = '';
  categoryTabId: VideoCategoryId;
  categoryTabIds = VideoCategoryId;
  selected = {startDate: dayjs().subtract(6, 'days'), endDate: dayjs()};
  statusTabId: VideoStatusId;
  statusTabIds = VideoStatusId;
  private readonly callFunction = new Subject<void>();
  constructor(private readonly functions: Functions, private readonly firestore: Firestore) {
    this.categoryTabId = this.categoryTabIds.STREAM;
    this.statusTabId = this.statusTabIds.SCHEDULED;
  }

  async createLiveStream(streamObj: Stream) {
    const createLivestream = httpsCallable(
      this.functions,
      'livestreams-createLivestream'
    );
    return (
      await createLivestream({
        livestream: streamObj,
      })
    ).data;
  }

  getVideoByDocId(channelDocId: string, streamId: string, until$?: Observable<unknown>): Observable<VideoWithDocId|undefined> {
    return getVideo$(this.firestore, streamId, channelDocId, until$ || EMPTY);
  }

  updateVideo(channelId: string, videoId: string, obj: any) {
    const videoDocRef = doc(
      this.firestore,
      `channels/${channelId}/videos`,
      videoId
    );
    return updateDoc(videoDocRef, obj);
  }

  removeStreamTag(streamId: string, tag: string) {
    const streamDocRef = doc(this.firestore, 'livestreams', streamId);
    return updateDoc(streamDocRef, {
      tags: arrayRemove(tag),
    });
  }

  removeShortsUploadsTag(channelId: string, videoId: string, tag: string) {
    const videoDocRef = doc(
      this.firestore,
      'channels',
      channelId,
      'videos',
      videoId
    );
    return updateDoc(videoDocRef, {
      tags: arrayRemove(tag),
    });
  }

  removeMediaConversionJobsUploadsTag(videoId: string, tag: string) {
    const videoDocRef = doc(this.firestore, 'mediaConversionJobs', videoId);
    return updateDoc(videoDocRef, {
      tags: arrayRemove(tag),
    });
  }

  addStreamTag(streamId: string, tags: string[]) {
    const streamDocRef = doc(this.firestore, 'livestreams', streamId);
    return updateDoc(streamDocRef, {
      tags: arrayUnion(...tags),
    });
  }

  addShortsUploadsTag(channelId: string, videoId: string, tags: string[]) {
    const videoDocRef = doc(
      this.firestore,
      'channels',
      channelId,
      'videos',
      videoId
    );
    return updateDoc(videoDocRef, {
      tags: arrayUnion(...tags),
    });
  }
  addMediaConversionJobsUploadsTag(videoId: string, tags: string[]) {
    const videoDocRef = doc(this.firestore, 'mediaConversionJobs', videoId);
    return updateDoc(videoDocRef, {
      tags: arrayUnion(...tags),
    });
  }
  getVideoSourceBydateRange(
    docId: string,
    dateRange: {
      fromDate: string;
      toDate: string;
    }
  ) {
    const videosCollection = collection(
      this.firestore,
      'channels',
      docId,
      'videos'
    );
    let q: Query<DocumentData>;

    if (dateRange.fromDate && dateRange.toDate) {
      q = query(
        videosCollection,
        orderBy('uploadTime', 'desc'),
        where('uploadTime', '>=', new Date(dateRange.fromDate)),
        where('uploadTime', '<=', new Date(dateRange.toDate))
      );
    } else if (dateRange.fromDate) {
      q = query(
        videosCollection,
        orderBy('uploadTime', 'desc'),
        where('uploadTime', '>=', new Date(dateRange.fromDate))
      );
    } else if (dateRange.toDate) {
      q = query(
        videosCollection,
        orderBy('uploadTime', 'desc'),
        where('uploadTime', '<=', new Date(dateRange.toDate))
      );
    } else {
      q = query(videosCollection, orderBy('uploadTime', 'desc'));
    }

    return collectionData(q, { idField: 'id' });
  }

  async copyVideoLink(
    livestreamDocIds: string[],
    type: 'LIVESTREAM' | 'CLIP'
  ) {
    const getDownloadLinks = httpsCallable(
      this.functions,
      'livestreams-getDownloadLinks'
    );
    return (
      await getDownloadLinks({
        type: type,
        livestreamDocIds: livestreamDocIds,
      })
    ).data as any;
  }

  sendClickEvent() {
    this.callFunction.next();
  }

  getClickEvent() {
    return this.callFunction.asObservable();
  }

  getAccessLogEntries(channelDocId: string, videoId: string) {
    const entriesCollection = collection(
      this.firestore,
      'channels',
      channelDocId,
      'videos',
      videoId,
      'access-log-entries'
    );
    return getDocs(entriesCollection).then((querySnapshot) => querySnapshot.docs.map((doc) => {
        const data = doc.data() as VideoAccessLogEntry;
        const docId = doc.id;
        return { docId, ...data };
      }));
  }

  async getPlansByBatch(channelDocId: string, idList: string[]) {
    return (
      await Promise.all(
        chunk(idList, 10).map(slice => {
          const plansCollection = collection(
            this.firestore,
            'channels',
            channelDocId,
            'plans'
          );
          const q = query(plansCollection, where(documentId(), 'in', slice));
          return getDocs(q);
        })
      )
    ).reduce((acc: any, batchResult) => [...acc, 
      ...batchResult.docs.map((doc) => ({ docId: doc.id, ...doc.data() }))], []);
  }

  getStreamsByClubId(id: string) {
    const livestreamsCollection = collection(this.firestore, 'livestreams');
    const q = query(livestreamsCollection, where('clubDocId', '==', id));

    return collectionData(q).pipe(
      map((changes) => changes.map((a) => ({ docId: a.id, ...a })))
    );
  }
  
  getStreamTimes(item: Stream) {
    const startTime = dayjs((item.scheduledStartTime as Timestamp).toDate());
    const stopTime = dayjs((item.scheduledStopTime as Timestamp).toDate());
    const createdTime = dayjs((item.createdTime as Timestamp).toDate());
    const timeDiff = Math.abs(stopTime.valueOf() - startTime.valueOf());
    const durationMins = Math.ceil(Math.ceil(timeDiff / 1000) / 60);

    return {
      startTime,
      stopTime,
      createdTime,
      timeDiff,
      durationMins,
      durationStr: `${durationMins} Mins`,
    };
  }

  buildDateFields(startTime: dayjs.Dayjs, stopTime: dayjs.Dayjs, createdTime: dayjs.Dayjs, durationStr: string) {
    return {
      'Start Month': startTime.format('MMM'),
      'Start Date': startTime.format('DD'),
      'Start Year': startTime.format('YYYY'),
      'Start Time': startTime.format('HH:mm'),
      'Stop Month': stopTime.format('MMM'),
      'Stop Date': stopTime.format('DD'),
      'Stop Year': stopTime.format('YYYY'),
      'Stop Time': stopTime.format('HH:mm'),
      Duration: durationStr,
      'Created Month': createdTime.format('MMM'),
      'Created Date': createdTime.format('DD'),
      'Created Year': createdTime.format('YYYY'),
      'Created Time': createdTime.format('HH:mm'),
    };
  }

  async getVideosFromSource(
    videoSource: VideoListSource,
    videoQueryOption: VideoSourceQueryOption
  ) {
    return await lastValueFrom(
      getVideosFromSource(this.firestore, videoSource, videoQueryOption)
    );
  }

  getRtmpOrSrUrl(reservedInput: ReservedInputParam[], id?: string): string {
    if (!id) {return '';}
    return (
      reservedInput.find((item: ReservedInputParam) => item.id === id)
        ?.rtmpUrl || reservedInput.find((item: ReservedInputParam) => item.id === id)?.srtUrl || ''
    );
  }
}
